import { decorate, inject, injectable } from 'inversify'
import { makeAutoObservable, runInAction } from 'mobx'
import { APIDataBlueUser, APIDataIntegration, APIDataIntegrationACL } from '@evertel/types'
import { Api } from '@evertel/api'
import { IntegrationStore } from '@evertel/stores'
import { DEFAULT_USER_SCOPE, FetchUserService } from '@evertel/blue-user'

class RoomConnectorsController {
    roomId = 0
    departmentId = 0
    integrationAcl: APIDataIntegrationACL[] = []
    private _togglingIds: Set<number> = new Set()
    private _activeKeyIntegrationIds: Set<number> = new Set()

    constructor(
        private api: Api,
        private integrationStore: IntegrationStore,
        private fetchUserService: FetchUserService
    ) {
        makeAutoObservable(this)
    }

    init = (roomId: number, departmentId: number, roomIntegrationAcl: APIDataIntegrationACL[]) => {
        if (!roomId || !departmentId || !roomIntegrationAcl) return

        this.roomId = roomId
        this.departmentId = departmentId
        this.integrationAcl = roomIntegrationAcl
    }

    /**
     * Fetch all department integrations (for availableConnectors list).
     * Only needed when an executive opens the connectors panel.
     */
    fetchDepartmentIntegrations = async () => {
        try {
            const integrations = await this.api.Routes.Department.getIntegrations(this.departmentId, {
                include: {
                    relation: 'integrationUser',
                    scope: {
                        ...DEFAULT_USER_SCOPE
                    }
                }
            })

            // Store integration users in BlueUserStore so they're accessible like any other user
            const integrationUsers = integrations
                .map(i => i.integrationUser)
                .filter((u): u is APIDataBlueUser => !!u)
            if (integrationUsers.length) {
                this.fetchUserService.addRecentlyFetchedUsers(integrationUsers)
            }

            runInAction(() => {
                this.integrationStore.update(integrations)
            })
        } catch {
            // silently fail
        }
    }

    fetchActiveKeyCounts = async () => {
        try {
            const integrations = this.departmentIntegrations

            const activeKeyCounts = await Promise.all(
                integrations.map(i => this.api.Routes.Integration.getApiKeysCount(i.id, { status: 'active' }))
            )

            runInAction(() => {
                this._activeKeyIntegrationIds = new Set(
                    integrations
                        .filter((_, idx) => activeKeyCounts[idx].count > 0)
                        .map(i => i.id)
                )
            })
        } catch {
            // silently fail
        }
    }

    hasActiveKeys = (integrationId: number): boolean => {
        return this._activeKeyIntegrationIds.has(integrationId)
    }

    toggleConnector = async (integrationId: number) => {
        if (!this.roomId || !integrationId) return

        runInAction(() => {
            this._togglingIds.add(integrationId)
        })

        try {
            const existingAcls = this.integrationAcl.filter(acl => acl.integrationId === integrationId)

            if (existingAcls.length > 0) {
                // Disable via single bulk endpoint. Server owns the
                // soft-delete transaction and event-message emission.
                const result = await this.api.Routes.Room.postIntegrationsDisable(this.roomId, integrationId)
                if (result && result.deleted >= 0) {
                    runInAction(() => {
                        this.integrationAcl = this.integrationAcl.filter(acl => acl.integrationId !== integrationId)
                    })
                }
            } else {
                // Enable via single bulk endpoint. The server uses its
                // serviceType registry (or caller-supplied accessTypes
                // for custom integrations) to create the right set of
                // ACLs and emit a single event message.
                const result = await this.api.Routes.Room.postIntegrationsEnable(this.roomId, integrationId)
                if (result && Array.isArray(result.aclEntries)) {
                    runInAction(() => {
                        this.integrationAcl = [
                            ...this.integrationAcl.filter(acl => acl.integrationId !== integrationId),
                            ...result.aclEntries
                        ]
                    })
                }
            }
        } catch (err) {
            console.warn('RoomConnectorsController: toggleConnector failed', err)
        } finally {
            runInAction(() => {
                this._togglingIds.delete(integrationId)
            })
        }
    }

    isConnectorEnabled = (integrationId: number): boolean => {
        return this.integrationAcl.some(acl => acl.integrationId === integrationId)
    }

    isToggling = (integrationId: number): boolean => {
        return this._togglingIds.has(integrationId)
    }

    get hasActiveConnectors(): boolean {
        return this.integrationAcl.length > 0
    }

    get departmentIntegrations(): APIDataIntegration[] {
        return this.integrationStore.objectsArray.filter(
            i => i.departmentId === this.departmentId
        )
    }

    get enabledConnectors(): APIDataIntegration[] {
        return this.departmentIntegrations.filter(i => this.isConnectorEnabled(i.id))
    }

    get availableConnectors(): APIDataIntegration[] {
        return this.departmentIntegrations.filter(i => !this.isConnectorEnabled(i.id))
    }
}

let i = 0
decorate(injectable(), RoomConnectorsController)
decorate(inject(Api), RoomConnectorsController, i++)
decorate(inject(IntegrationStore), RoomConnectorsController, i++)
decorate(inject(FetchUserService), RoomConnectorsController, i++)

export { RoomConnectorsController }
